import * as React from 'react';
import styled, { css } from 'styled-components';
import { Card, Text } from '@amzn/storm-ui';
import {
  AdvertisingAccountType,
  CentricAccountTypeName,
} from '@amzn/aac-chrome-internal-api';
import { gray100 } from 'src/colors';
import { useAccountDropdownContext } from './AccountDropdownProvider';
import { useAccountDropdownBundle } from 'src/providers/PantherLocalizationProvider';
import { getCountyCodeByMarketplace } from 'src/commons/utils/advertising-config-store/ACSConfigClient';
import { ACCOUNT_CARD_TYPE } from './AccountTypeLocalizationKeyMapping';
import {
  findSelectedAccountSwitcherAccount,
  findSelectedCentricAccountSwitcherAccount,
} from './accountHelper';

import { useCentricAccountDropdownSearch } from './CentricAccountDropdown/useCentricAccountDropdownSearchAndFilter';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';

export const StyledCard = styled(Card)`
  background: ${gray100};
  box-shadow: none;
`;

const InfoCardWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.base};
  background-color: ${({ theme }) => theme.palette.gray[25]};
  border-bottom: 1px solid ${({ theme }) => theme.palette.gray[150]};
`;

export const PropertyPairContainer = styled.div`
  line-height: 18px;

  &:not(:last-of-type) {
    margin-bottom: 3px;
  }

  & > *:first-of-type {
    display: inline-block;
  }
`;

export const ColonText = styled(Text)`
  margin-inline-end: 2px;
`;

export const WrappedText = styled(Text)<{ bold?: boolean }>`
  ${({ bold }) =>
    bold &&
    css`
      font-weight: 700;
    `}
  word-break: break-all;
  line-height: 18px;
`;

export const AccountNameWrapper = styled(WrappedText)`
  margin-bottom: 3px;
  line-height: 21px;
`;

export const PropertyPair = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.ReactElement => {
  return (
    <PropertyPairContainer>
      <Text fontSize="small" inline textColor="tertiary">
        {label}
      </Text>
      <ColonText fontSize="small" inline>
        :
      </ColonText>
      <WrappedText fontSize="small" inline>
        {value}
      </WrappedText>
    </PropertyPairContainer>
  );
};

export const IA2AccountDropdownInfoCard: React.VFC = () => {
  const {
    currentAccountName,
    currentAccountType,
    currentMarketplaceId,
    currentEntityId,
    accounts,
    isManagerAPartner,
    retailerName,
  } = useAccountDropdownContext();
  const bundle = useAccountDropdownBundle();
  const isMagellanEnabled = useIsFeatureEnabled(
    WeblabedFeatures.MAGELLAN_RETAILER_HEADER,
  );
  const singleMarketplaceLabelKey =
    getCountyCodeByMarketplace(currentMarketplaceId);
  const singleMarketplaceLabel = singleMarketplaceLabelKey
    ? bundle.getMessage(
        `account-switcher-marketplace-${singleMarketplaceLabelKey}`,
      )
    : '';
  const selectedAccount = findSelectedAccountSwitcherAccount(
    currentEntityId,
    accounts.map((accountGroup) => accountGroup.accounts).flat(),
  );
  const nestedAccounts = selectedAccount?.nestedAccounts;
  const marketplaceLabel =
    (nestedAccounts && nestedAccounts.length > 1) ||
    currentAccountType === AdvertisingAccountType.DSP_ENTITY
      ? bundle.getMessage('account-switcher-multiple-countries')
      : singleMarketplaceLabel;

  const currentAccountTypeLabel = bundle.getMessage(
    ACCOUNT_CARD_TYPE.get(currentAccountType) || '',
  );

  const contents: React.ReactElement = (
    <>
      <AccountNameWrapper type="h3">
        {selectedAccount?.label || currentAccountName}
      </AccountNameWrapper>

      {!isMagellanEnabled ? (
        <PropertyPair
          label={bundle.getMessage('account-dropdown-current-type-label')}
          value={currentAccountTypeLabel}
        />
      ) : (
        <WrappedText fontSize="small">{currentAccountTypeLabel}</WrappedText>
      )}

      {isMagellanEnabled && retailerName && (
        <WrappedText fontSize="small" bold>
          {retailerName}
        </WrappedText>
      )}

      {currentAccountType !== AdvertisingAccountType.MANAGER &&
        currentAccountType !== AdvertisingAccountType.DSP_ENTITY &&
        (!isMagellanEnabled ? (
          <PropertyPair
            label={bundle.getMessage('account-dropdown-country-label')}
            value={marketplaceLabel}
          />
        ) : (
          <WrappedText fontSize="small">{marketplaceLabel}</WrappedText>
        ))}

      {!isMagellanEnabled && (
        <PropertyPair
          label={
            isManagerAPartner
              ? bundle.getMessage('account-dropdown-partner-id-label')
              : bundle.getMessage('account-dropdown-entity-id-label')
          }
          value={selectedAccount?.entityId || currentEntityId}
        />
      )}
    </>
  );

  if (isMagellanEnabled) {
    return <InfoCardWrapper>{contents}</InfoCardWrapper>;
  }

  return <StyledCard padding="base">{contents}</StyledCard>;
};

export const CentricAccountDropdownInfoCard: React.VFC = () => {
  const {
    currentAccountName,
    currentAccountType,
    currentMarketplaceId,
    currentEntityId,
    centricAccounts,
    centricNextToken,
    isManagerAPartner,
  } = useAccountDropdownContext();
  const { accounts } = useCentricAccountDropdownSearch({
    centricAccounts,
    centricNextToken,
  });
  const bundle = useAccountDropdownBundle();
  const singleMarketplaceLabelKey =
    getCountyCodeByMarketplace(currentMarketplaceId);
  const singleMarketplaceLabel = singleMarketplaceLabelKey
    ? bundle.getMessage(
        `account-switcher-marketplace-${singleMarketplaceLabelKey}`,
      )
    : '';
  const selectedAccount = findSelectedCentricAccountSwitcherAccount(
    currentEntityId,
    accounts,
  );
  //TODO: update to read CENTRIC_ACCOUNT_CARD_TYPE when Sauron currentAccountType align with CENTRIC
  const marketplaceLabel =
    selectedAccount?.accountType === CentricAccountTypeName.GLOBAL
      ? bundle.getMessage('account-switcher-multiple-countries')
      : singleMarketplaceLabel;
  return (
    <StyledCard padding="base">
      <AccountNameWrapper type="h3">
        {selectedAccount?.accountName || currentAccountName}
      </AccountNameWrapper>
      <PropertyPair
        label={bundle.getMessage('account-dropdown-current-type-label')}
        value={bundle.getMessage(
          ACCOUNT_CARD_TYPE.get(currentAccountType) || '',
        )}
      />
      {currentAccountType !== CentricAccountTypeName.MANAGER && (
        <PropertyPair
          label={bundle.getMessage('account-dropdown-country-label')}
          value={marketplaceLabel}
        />
      )}
      <PropertyPair
        label={
          isManagerAPartner
            ? bundle.getMessage('account-dropdown-partner-id-label')
            : bundle.getMessage('account-dropdown-entity-id-label')
        }
        value={selectedAccount?.accountId || currentEntityId}
      />
    </StyledCard>
  );
};
